import React, { useState } from "react";
import * as R from "ramda";
import { FormattedMessage } from "react-intl";
import { Button, Box, Grid, Icon, Text, Tooltip, HStack } from "@chakra-ui/react";

import { useOverlaySpinner } from "components/OverlaySpinner";
import { PaypalLogo } from "components/Logos";
import {
  exists,
  isNilOrEmpty,
  inIframe,
  useSessionJWT,
  useSessionCtx,
  usePostMessage,
  FLOWS,
  redirectWindowLocation,
  useAnalytics,
} from "utils";
import { ErrorMessageDiv } from "components/Form";
import { logError } from "api/accounts-customer";
import usePayPalRedirect from "components/Paypal/usePayPalRedirect";
import { BsPaypal } from "react-icons/bs";
import { IoIosAddCircleOutline } from "react-icons/io";

const tokenRegex = /token=(.+)/;
export const parseToken = (response) => {
  if (isNilOrEmpty(response)) return null;
  const uri = response.payload?.redirect_uri;
  if (isNilOrEmpty(uri)) return null;

  return R.pipe(
    R.prop("search"),
    R.match(tokenRegex),
    R.prop(1),
    // split off extra query string
    R.split("&"),
    R.prop(0),
  )(new URL(uri));
};

export const AddPaypalButton = ({
  amountExceedsMax,
  newUI,
  useTextUI,
  description,
  onPreClickCallback,
  prepareTokenOnLoad = true,
}) => {
  const analytics = useAnalytics();
  const { sessionJWT } = useSessionJWT();
  const { sessionCtx, debug, setFlowState } = useSessionCtx();
  const { flowState } = sessionCtx;
  const { postMessage } = usePostMessage(FLOWS.PAYPAL, sessionCtx?.intCtx);
  const [redirect, setRedirect] = useState();
  const { showOverlaySpinner, hideOverlaySpinner } = useOverlaySpinner();
  const [isLoading, setIsLoading] = useState(false);
  const { redirectToken, getPayPalRedirectResponse } = usePayPalRedirect(prepareTokenOnLoad);

  if (redirect) {
    debug("paypalbutton__redirect", redirect);
    redirectWindowLocation(redirect);
  }

  const processStartCreateResponse = (response) => {
    debug("paypalbutton__processresponse", response.payload);
    setFlowState("paypal.redirect");

    if (inIframe()) {
      postMessage(`tilia.${FLOWS.PAYPAL}.redirect`, { redirect: response.payload.redirect_uri });
    } else {
      setRedirect(response.payload.redirect_uri);
    }
  };

  const processError = async (error) => {
    await logError(sessionJWT, { flowLoc: "PAYPAL.START_CREATE", error });
    if (inIframe()) {
      postMessage(`tilia.${FLOWS.PAYPAL}.error`, { error });
    } else {
      // redirect error?
      debug("paypalbutton__fullpage__error?", error);
    }
  };

  const handleClick = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      analytics.track("PayPal_Add_New_Button_Clicked");
      showOverlaySpinner();

      const startCreateResponse = await getPayPalRedirectResponse();
      processStartCreateResponse(startCreateResponse);
    } catch (error) {
      processError(error);
    }
    setIsLoading(false);
    hideOverlaySpinner();
  };

  // TODO: Looks like we currently have 3 different views of this component.  Should limit that down
  let content = (
    <Tooltip label={amountExceedsMax ? "Amount exceeds maximum allowed" : ""} shouldWrapChildren>
      <Button
        data-testid="pm-add-paypal-button"
        variant="outline"
        colorScheme="gray"
        onClick={handleClick}
        isDisabled={isLoading || amountExceedsMax || !exists(redirectToken)}
        width="full"
      >
        <Box mr={2} as="span">
          <FormattedMessage defaultMessage="Add" description="button label" />
        </Box>
        <PaypalLogo
          css={`
            height: 20px;
            width: auto;
          `}
        />
      </Button>
    </Tooltip>
  );

  if (useTextUI) {
    content = (
      <HStack
        spacing="1"
        color="integrator.400"
        fontWeight="600"
        cursor="pointer"
        onClick={() => {
          if (onPreClickCallback) onPreClickCallback();
          handleClick();
        }}
      >
        <Icon as={IoIosAddCircleOutline} w="5" h="5" />
        <Text>
          <FormattedMessage defaultMessage="Add PayPal" description="button label" />
        </Text>
      </HStack>
    );
  } else if (newUI) {
    content = (
      <Box
        data-testid="pm-add-paypal-button"
        p="4"
        boxShadow="base"
        cursor="pointer"
        onClick={handleClick}
      >
        <Grid templateColumns="20px 1fr" gridGap="2" alignItems="center">
          <Icon as={BsPaypal} boxSize="5" color="integrator.500" />
          <Text fontWeight="bold">
            <FormattedMessage defaultMessage="Add" description="button label" /> PayPal
          </Text>
          <Box />
          <Box>{description}</Box>
        </Grid>
      </Box>
    );
  }

  return (
    <>
      {content}
      {redirect && <span data-testid="add-paypal-redirect"></span>}
      {flowState === "paypal.error" && (
        <ErrorMessageDiv
          css={`
            margin: 8px 0;
          `}
        >
          <FormattedMessage
            defaultMessage="There was an error with your request. Please try again."
            description="Error message"
          />
        </ErrorMessageDiv>
      )}
    </>
  );
};
